import { DocumentType, modelOptions, post, pre, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { doesDeviceVersionMatchSelector } from "../../utils/VerdiDeviceVersionUtilities.js";
import {
    getRegexToMatchNegativeVersionSelectorFromVersion,
    getRegexToMatchPositiveVersionSelectorFromVersion,
} from "../../utils/verdiDeviceVersionRegexUtilities.js";
import {
    getZodSchemaFromClass,
    HardwareVariantString,
    ProductLineString,
    validate,
} from "../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type { DeviceVersionFieldsWithDefaults, DeviceVersionSchemaTypes } from "../types/deviceVersion.d.js";
import { type DtoValidatorTypes } from "../validatableClass.js";

const SCHEMA_KEY = "DeviceVersion" as const;

// Forward declarations to avoid circular dependencies
type Generation = number;
export type ProductLine = "block" | "micro" | "mega" | "mini";
export type HardwareVariant =
    | "a"
    | "b"
    | "c"
    | "d"
    | "e"
    | "f"
    | "g"
    | "h"
    | "i"
    | "j"
    | "k"
    | "l"
    | "m"
    | "n"
    | "o"
    | "p"
    | "q"
    | "r"
    | "s"
    | "t"
    | "u"
    | "v"
    | "w"
    | "x"
    | "y"
    | "z";
type HardwareVersion = number;
type FirmwareVersion = number;

/**
 * Database optimization to allow querying for things that support this device type without needing to make
 * sequential queries
 */
@pre<DeviceVersion>("save", async function saveDeviceVersion(this: DocumentType<DeviceVersion>) {
    try {
        DeviceVersion.utilities.populateRegex(this);
    } catch (e) {
        console.warn("Error populating version selector regex", e);
    }
})
@pre<DeviceVersion>("validate", async function saveDeviceVersion(this: DocumentType<DeviceVersion>) {
    try {
        DeviceVersion.utilities.populateRegex(this);
    } catch (e) {
        console.warn("Error populating version selector regex", e);
    }
})
@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<DeviceVersion>>(...addSchemaKeyHook(SCHEMA_KEY))
export class DeviceVersion extends SchemaBase<DeviceVersion, DeviceVersionFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: z.number() })
    @prop({ type: () => Number, required: true })
    public generation!: Generation;

    @validate({ zodValidator: ProductLineString })
    @prop({ type: () => String, required: true })
    public productLine!: ProductLine;

    @validate({
        zodValidator: HardwareVariantString,
    })
    @prop({ type: () => String, required: true })
    public hardwareVariant!: HardwareVariant;

    @validate({ zodValidator: z.number() })
    @prop({ type: () => Number, required: true })
    public hardwareVersion!: HardwareVersion;

    @validate({ zodValidator: z.number() })
    @prop({ type: () => Number, required: true })
    public firmwareVersion!: FirmwareVersion;

    /**
     * This is a property that allows us to query the database for objects with version selectors that would include this event.
     * Should be queried like this:
     *
     * {
     *      $regex: $_regexToMatchPositiveVersionSelector,
     *      $options: "i",
     *         $not: {
     *             $regex: $_regexToMatchNegativeVersionSelector,
     *             $options: "i",
     *         },
     * }
     *
     * This will always be present in the database, but will not always be present once loaded due to new version objects
     * not getting it until after being saved to the DB. That is why this is marked private and is required in typegoose
     * but not in typescript
     * @private
     */
    @validate({ zodValidator: z.string().optional(), responseOnly: true })
    @prop({ type: () => String, required: true })
    private _regexToMatchPositiveVersionSelector?: string;

    /**
     * This is a property that allows us to query the database for objects with version selectors that would include this event.
     * Should be queried like this:
     *
     * {
     *      $regex: $_regexToMatchPositiveVersionSelector,
     *      $options: "i",
     *         $not: {
     *             $regex: $_regexToMatchNegativeVersionSelector,
     *             $options: "i",
     *         },
     * }
     *
     * This will always be present in the database, but will not always be present once loaded due to new version objects
     * not getting it until after being saved to the DB. That is why this is marked private and is required in typegoose
     * but not in typescript
     * @private
     *
     * 4.block.5+.a.4:8
     */
    @validate({ zodValidator: z.string().optional(), responseOnly: true })
    @prop({ type: () => String, required: true })
    private _regexToMatchNegativeVersionSelector?: string;

    static getters = {
        getDeviceVersionString: (self: DeviceVersion): string =>
            `${self.generation}.${self.productLine}.${self.hardwareVersion}.${self.hardwareVariant}.${self.firmwareVersion}`,
        isDeviceVersionComplete: (self: DeviceVersion): boolean =>
            self.generation !== undefined &&
            self.productLine !== undefined &&
            self.hardwareVersion !== undefined &&
            self.hardwareVariant !== undefined &&
            self.firmwareVersion !== undefined,
    } as const satisfies Record<string, (self: DeviceVersion) => any>;

    static utilities = {
        doesDeviceVersionMatchVersionSelector: (deviceVersion: DeviceVersion, versionSelector: string): boolean => {
            const deviceVersionObject: DeviceVersion = {
                schemaKey: DeviceVersion.schemaKey,
                generation: deviceVersion.generation,
                productLine: deviceVersion.productLine,
                hardwareVariant: deviceVersion.hardwareVariant,
                hardwareVersion: deviceVersion.hardwareVersion,
                firmwareVersion: deviceVersion.firmwareVersion,
            };
            return doesDeviceVersionMatchSelector(deviceVersionObject, versionSelector);
        },
        populateRegex: (deviceVersion: DeviceVersion): void => {
            const positiveVersionSelector = getRegexToMatchPositiveVersionSelectorFromVersion(deviceVersion);
            const negativeVersionSelector = getRegexToMatchNegativeVersionSelectorFromVersion(deviceVersion);

            // gated behind if statements to prevent unnecessary "mark modified" calls
            if (positiveVersionSelector !== deviceVersion._regexToMatchPositiveVersionSelector) {
                deviceVersion._regexToMatchPositiveVersionSelector = positiveVersionSelector;
            }
            if (negativeVersionSelector !== deviceVersion._regexToMatchNegativeVersionSelector) {
                deviceVersion._regexToMatchNegativeVersionSelector = negativeVersionSelector;
            }
        },
    } as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<DeviceVersionSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<DeviceVersion, DeviceVersionFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

DeviceVersion.dtoValidators = getZodSchemaFromClass(
    DeviceVersion,
) as unknown as DtoValidatorTypes<DeviceVersionSchemaTypes>;
